/**
 * `<AssignPopover/>` — assign / unassign members on a node (B2.6).
 *
 * Issue #760 (Phase 2). The richer successor to #759's minimal member
 * picker: a titled card ("Assign to '<node>'") with a search field, the team
 * roster as avatar + name rows, an "✓ assigned" pill on those already on the
 * node and an "add" button on those not, and a calm footer line. Clicking a
 * row toggles assignment via the POST/DELETE mutations the editor wires in
 * (one event each). This is the **one** assign UI in task-mode — the node
 * card (B2.2), the outline rows, and the detail drawer's "Assigned to" row
 * (B2.5) all use it; it is never forked.
 *
 * §A5: assigning grants authoring rights on the subtree — the footer states
 * this factually. §A9: this is "who's on this", never a score or ranking —
 * no numbers about a person appear.
 *
 * Editorial revamp (#1043, Wave 1 · B2.6): theme-aware. The style forks on the
 * per-team `task_editorial_ui` flag via `useMapTheme()`:
 *   - LEGACY (flag off): byte-identical to today — soft-mint avatar discs,
 *     teal search-field border on a `#F7F8FA` wash, the `rollupBg` "✓ assigned"
 *     pill.
 *   - EDITORIAL (flag on): the wireframe `V_B26` — a `--surface` card on a
 *     `--rule` hairline with a soft drop-shadow, a mono `ASSIGN TO "…"` header
 *     (`<Label>`), a borderless mono search input, theme-aware `<Avatar/>`
 *     discs, a teal "add" / sage uppercased-mono "✓ assigned" affordance, and
 *     the assigned row tinted `--st-done-bg` (#DDE9D9).
 * Structure, copy, every `data-testid`, and behaviour are unchanged.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import { PALETTE } from "../tree/palette";
import { AvatarRow } from "./Avatar";

export interface AssignPopoverProps {
  /** The node's title — shown in the header ("Assign to '<title>'"). */
  nodeTitle: string;
  /** The full team roster to choose from. */
  members: TaskMember[];
  /** Membership ids currently assigned to the node. */
  assignedIds: string[];
  /** Toggle a membership on/off the node (assign=true ⇒ POST, false ⇒ DELETE). */
  onToggle: (membershipId: string, assign: boolean) => void;
  /** Dismiss the popover. */
  onClose: () => void;
  /**
   * Layout: a free-floating modal-style card (the gallery's B2.6, used from
   * the drawer) or an anchored dropdown (attached under a card's @assign
   * button). Defaults to the anchored dropdown.
   */
  variant?: "anchored" | "panel";
}

export function AssignPopover({
  nodeTitle,
  members,
  assignedIds,
  onToggle,
  onClose,
  variant = "anchored",
}: AssignPopoverProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  // The sage "done" state colours carry the "✓ assigned" affordance + the
  // assigned-row tint in editorial (the wireframe's `--st-done` / `--st-done-bg`).
  const done = theme.stateStyle("done");

  const [query, setQuery] = useState("");
  const assigned = useMemo(() => new Set(assignedIds), [assignedIds]);
  const searchRef = useRef<HTMLInputElement>(null);

  // Focus the search on open (matches the gallery's active cursor) — done via
  // ref rather than `autoFocus` so it stays calm + a11y-clean.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) => m.name.toLowerCase().includes(q) || m.initials.toLowerCase().includes(q),
    );
  }, [members, query]);

  // Editorial: the wireframe card has section dividers (header / search / list /
  // footer) instead of inner padding, on a `--rule` hairline + soft shadow.
  // Legacy: byte-identical padded card.
  const wrapperClass = editorial
    ? variant === "panel"
      ? "w-[340px] overflow-hidden rounded-lg border"
      : "absolute z-30 mt-1 w-[300px] overflow-hidden rounded-lg border"
    : variant === "panel"
      ? "w-[340px] rounded-lg border bg-white p-4 shadow-xl"
      : "absolute z-30 mt-1 w-[300px] rounded-lg border bg-white p-3 shadow-xl";

  return (
    <div
      data-testid="assign-popover"
      className={wrapperClass}
      style={
        editorial
          ? {
              borderColor: c.rule,
              backgroundColor: c.cardBg,
              borderRadius: theme.radii.lg,
              boxShadow: "0 12px 36px rgba(0,0,0,0.12)",
              fontFamily: theme.fonts.sans,
            }
          : { borderColor: PALETTE.cardBorder }
      }
      aria-label={`Assign to ${nodeTitle}`}
    >
      {editorial ? (
        <div
          className="border-b px-3.5 py-3"
          style={{ borderColor: c.rule }}
          data-testid="assign-popover-title"
        >
          <span
            className="text-[10px] font-medium uppercase"
            style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.1em", color: c.muted }}
          >
            Assign to “{nodeTitle}”
          </span>
        </div>
      ) : (
        <div
          className="text-sm font-bold"
          style={{ color: PALETTE.ink }}
          data-testid="assign-popover-title"
        >
          Assign to “{nodeTitle}”
        </div>
      )}

      {/* Search field */}
      {editorial ? (
        <div className="border-b px-3.5 py-2" style={{ borderColor: c.rule }}>
          <input
            ref={searchRef}
            data-testid="assign-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people…"
            aria-label="Search people"
            className="w-full bg-transparent text-sm outline-none"
            style={{ color: c.ink, fontFamily: theme.fonts.mono }}
          />
        </div>
      ) : (
        <div
          className="mt-2 flex items-center gap-2 rounded-lg border px-2.5 py-2"
          style={{ borderColor: PALETTE.teal, backgroundColor: "#F7F8FA" }}
        >
          <span aria-hidden style={{ color: PALETTE.muted }}>
            ⌕
          </span>
          <input
            ref={searchRef}
            data-testid="assign-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people…"
            aria-label="Search people"
            className="w-full bg-transparent text-sm outline-none"
            style={{ color: PALETTE.ink }}
          />
        </div>
      )}

      {/* Member list */}
      <ul
        className={editorial ? "max-h-60 overflow-auto p-1.5" : "mt-2 max-h-60 overflow-auto"}
        data-testid="assign-list"
      >
        {members.length === 0 && (
          <li className="px-1 py-3 text-xs" style={{ color: editorial ? c.faint : PALETTE.faint }}>
            No team members yet.
          </li>
        )}
        {members.length > 0 && filtered.length === 0 && (
          <li className="px-1 py-3 text-xs" style={{ color: editorial ? c.faint : PALETTE.faint }}>
            No one matches “{query.trim()}”.
          </li>
        )}
        {filtered.map((m) => {
          const isOn = assigned.has(m.membershipId);
          if (editorial) {
            // Editorial row: theme-aware avatar disc, assigned row tinted
            // `--st-done-bg`, teal "add" / sage uppercased-mono "✓ assigned".
            return (
              <li
                key={m.membershipId}
                data-testid="assign-row"
                data-membership-id={m.membershipId}
                data-assigned={isOn}
                className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-2"
                style={{ backgroundColor: isOn ? done.chipBg : "transparent" }}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <AvatarRow members={[m]} size={32} />
                  <span
                    className={`truncate text-sm ${isOn ? "font-semibold" : ""}`}
                    style={{ color: c.ink }}
                  >
                    {m.name}
                  </span>
                </span>
                {isOn ? (
                  <button
                    type="button"
                    data-testid="assign-remove"
                    aria-label={`Remove ${m.name}`}
                    onClick={() => onToggle(m.membershipId, false)}
                    title="Remove"
                    className="shrink-0 text-[11.5px] font-medium uppercase transition-opacity hover:opacity-80"
                    style={{
                      color: done.dot ?? done.fg,
                      fontFamily: theme.fonts.mono,
                      letterSpacing: "0.06em",
                    }}
                  >
                    ✓ assigned
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="assign-add"
                    aria-label={`Add ${m.name}`}
                    onClick={() => onToggle(m.membershipId, true)}
                    className="shrink-0 text-[11.5px] transition-opacity hover:opacity-80"
                    style={{ color: c.teal }}
                  >
                    add
                  </button>
                )}
              </li>
            );
          }
          return (
            <li
              key={m.membershipId}
              data-testid="assign-row"
              data-membership-id={m.membershipId}
              data-assigned={isOn}
              className="flex items-center justify-between gap-2 border-b py-2 last:border-b-0"
              style={{ borderColor: "#F3F4F6" }}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <span
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
                  style={{ backgroundColor: PALETTE.avatarA, color: PALETTE.ink }}
                >
                  {m.initials}
                </span>
                <span
                  className={`truncate text-sm ${isOn ? "font-semibold" : ""}`}
                  style={{ color: PALETTE.ink }}
                >
                  {m.name}
                </span>
              </span>
              {isOn ? (
                <button
                  type="button"
                  data-testid="assign-remove"
                  aria-label={`Remove ${m.name}`}
                  onClick={() => onToggle(m.membershipId, false)}
                  title="Remove"
                  className="shrink-0 rounded-md px-2.5 py-1 text-[11px] font-semibold transition-opacity hover:opacity-80"
                  style={{ backgroundColor: PALETTE.rollupBg, color: PALETTE.rollupInk }}
                >
                  ✓ assigned
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="assign-add"
                  aria-label={`Add ${m.name}`}
                  onClick={() => onToggle(m.membershipId, true)}
                  className="shrink-0 rounded-md border px-3 py-1 text-[11px] transition-colors hover:bg-slate-50"
                  style={{ borderColor: PALETTE.softBorder, color: PALETTE.slate }}
                >
                  add
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {/* Footer: factual note that assignment is authoring (§A5). */}
      <div
        className={
          editorial
            ? "flex items-center justify-between gap-2 border-t px-3.5 py-2.5"
            : "mt-2 flex items-center justify-between gap-2 border-t pt-2"
        }
        style={{ borderColor: editorial ? c.rule : "#F3F4F6" }}
      >
        <span
          className={editorial ? "text-[11px] italic" : "text-[11px]"}
          style={{ color: editorial ? c.muted : PALETTE.faint }}
        >
          assignees can grow &amp; re-assign this branch
        </span>
        <button
          type="button"
          data-testid="assign-done"
          onClick={onClose}
          className="shrink-0 rounded px-2 py-1 text-[11px] transition-colors hover:bg-slate-50"
          style={{ color: editorial ? c.muted : PALETTE.muted }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
